import {
  ShippingCountry,
  groupCountriesByRegion,
} from "@/libs/getShippingCountries";
import { geShippingCountries } from "@/libs/countries/ge-shipping-countries";
import {
  countryDisplayName,
  countryKoreanName,
  countrySearchText,
} from "@/libs/countries/country-names";
import { FeatureFlagName, useIsFeatureOn } from "@/queries/feature-flag-query";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

/**
 * 자체 배송(mgmt) 목록에서 빼는 국가 — **GE 모드에서는 쓰지 않는다.**
 *
 * GE 모드에서는 국제 구간을 GE 가 배송하므로 GE 의 운영 여부가 기준이다
 * (`libs/countries/ge-shipping-countries`). 이 넷 중 VN·AM·BN 은 GE 운영국이라 GE 모드에서는 뜬다.
 */
const NOT_POSSIBLE_SET = new Set([
  "VN", // Vietnam
  "AM", // Armenia
  "BN", // Brunei
  "MM", // Myanmar
]);

type ByRegion = Record<string, ShippingCountry[]>;

/**
 * mgmt 행의 **이름만** Intl 영어로 바꾼다 — 목록·지역·코드는 그대로다 (#5839).
 *
 * 종전 이름(`englishCountryName` · `koreanCountryName` · `ownCountryName`)은 버리지 않고
 * 검색어로 넘긴다. `Turkey` · `Czech Republic` · `터키` 로 찾던 사용자가 계속 찾는다.
 */
function withIntlNames(c: ShippingCountry): ShippingCountry {
  const name = countryDisplayName(c.countryCode);
  return {
    ...c,
    englishCountryName: name,
    ownCountryName: name,
    koreanCountryName: countryKoreanName(c.countryCode),
    searchText: countrySearchText(c.countryCode, [
      c.englishCountryName,
      c.koreanCountryName,
      c.ownCountryName,
      c.searchText,
    ]),
  };
}

/**
 * 국가 스위처·장바구니 시트·GNB 메뉴·제안 배너·세금 판정이 **전부 이 훅을 거친다.**
 * 목록 출처를 바꾸려면 여기만 바꾼다.
 *
 * ```
 *   globalEEnabled === true   GE 운영국(−AN, +KR)          ← 국제 구간을 GE 가 배송
 *   그 외 (false · 로딩 중)     mgmt 자체 배송국 − 위 4개국    ← 종전 그대로
 * ```
 *
 * ★**`=== true` 로 좁게 판정한다** — 로딩 중(`null`)에 GE 목록을 보이면 플래그가 꺼진 환경에서도
 * 잠깐 GE 목록이 뜨고, 그때 고른 비배송국이 `bh_ccode` 로 박힌다(AGENTS.md §가격 「`=== true`」).
 *
 * ⚠️ GE 모드에서도 **서버 가드는 mgmt 목록을 본다**(`middleware.ts` 첫 방문 US 교정 ·
 * `/api/order/checkout`). 그대로 둔 이유: GE 결제는 `/international-checkout` 으로 가서 그 가드를
 * 지나지 않고, 첫 방문 교정은 US 로 떨어뜨리는 안전한 쪽이라 사용자가 스위처에서 다시 고르면 된다.
 */
export function useShippingCountries() {
  const geEnabled = useIsFeatureOn(FeatureFlagName.GlobalEEnabled) === true;

  const mgmt = useQuery({
    queryKey: ["/api/shops/shipping-countries/"],
    queryFn: async (): Promise<ByRegion> => {
      const res = await fetch(`/api/shops/shipping-countries/`);
      if (!res.ok) {
        throw new Error("Failed to fetch current shipping country");
      }
      return await res.json();
    },
    enabled: !geEnabled,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 90,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    select: (data): ByRegion =>
      Object.fromEntries(
        Object.entries(data).map(([region, countries]) => [
          region,
          countries
            .filter((c) => !NOT_POSSIBLE_SET.has(c.countryCode))
            .map(withIntlNames),
        ]),
      ),
  });

  // GE 목록은 커밋된 캐시에서 만든다 — 네트워크가 없으니 로딩도 없다.
  const ge = useMemo(
    () =>
      geEnabled ? groupCountriesByRegion(geShippingCountries()) : undefined,
    [geEnabled],
  );

  if (geEnabled) return { data: ge, isPending: false, error: null };
  return { data: mgmt.data, isPending: mgmt.isPending, error: mgmt.error };
}
